import { useMutation, useQuery } from '@tanstack/react-query';

import { RoleAccessResponse } from '../models/RoleAccessResponse';
import { TokenResponse } from '../models/TokenResponse';
import axiosClient from '../axiosBearerClient';
import { jwtDecode } from 'jwt-decode';
import { saveAuthData } from '../common/localeStorageUtils';

const extractUserFormToken = (token?: string | null) => {
  if (token) {
    const decodedToken = jwtDecode(token) as any;
    return {
      user: {
        email: decodedToken.email as string,
        phoneNumber: decodedToken.phone_number as string,
        language: decodedToken.language as string,
        lastName: decodedToken.given_name as string,
        firstName: decodedToken.family_name as string,
      },
    };
  }

  return { user: null };
};
export const useAuth = () => {
  const token = localStorage.getItem('access_token');
  return extractUserFormToken(token);
};
export const useGetToken = () => {
  const { isPending, error, data, isError, mutateAsync } = useMutation({
    retry: false,
    mutationFn: async ({
      authCodeParam,
      isSignUp,
      isEditProfile,
      isEditPwd,
    }: {
      authCodeParam?: string;
      isSignUp: boolean;
      isEditProfile: boolean;
      isEditPwd: boolean;
    }) => {
      const urlData = new URLSearchParams(window.location.search);
      const authCode = urlData.get('code') || authCodeParam;

      if (isEditPwd && (!authCode || authCode === 'null')) {
        return Promise.resolve(null);
      }

      const response = await axiosClient.post<TokenResponse>(`/auth/token`, {
        redirectUri: 'https%3A%2F%2Foauth.pstmn.io%2Fv1%2Fcallback',
        authorizationCode: authCode,
        isSignUp,
        isEditProfile,
        isChangePassword: isEditPwd,
        clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
      });
      saveAuthData(response.data);
      const user = extractUserFormToken(response.data.access_token).user;
      return Promise.resolve({ ...response.data, user });
    },
  });

  return {
    isPending,
    error,
    isError,
    data,
    mutateAsync,
  };
};

export const useGetRolesAccess = (sdpId?: String) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['access-rights', sdpId],
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: false,
    enabled: !!sdpId,
    queryFn: async () => {
      return await axiosClient.get<RoleAccessResponse>(`/auth/access-rights?sdpId=${sdpId}`);
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const usePostInvalidateToken = () => {
  const { isPending, error, data, isError, mutateAsync } = useMutation({
    retry: false,
    mutationFn: async () => {
      return await axiosClient.post(`/auth/token/invalidate`);
    },
  });

  return {
    isPending,
    error,
    isError,
    data,
    mutateAsync,
  };
};
